import { configureStore } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { envConfig } from '@src/utils/envConfig';
import type { ApiKeyResult, User } from '@talos/kyoko';
import { avaApiRESTBaseQuery } from './avaApiRESTBaseQuery';

/**
 * An API for interacting with the user entity. Upon mutating the user in some way, the api will re-request for the new user,
 * and then reflect that response in the state.
 */
export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: avaApiRESTBaseQuery,
  tagTypes: ['User'],
  endpoints: builder => ({
    getUser: builder.query<User, void>({
      query: () => ({ url: '/user', method: 'GET' }),
      transformResponse: (user: User) => {
        // In local environments only, the proxy code replaces the email address domain to match
        // the domain we are serving the app from.
        // Revert that here so that everywhere else in the app, the user's email address is correct.
        if (envConfig.VITE_AVA_ENV === 'local') {
          user = { ...user, Email: user.Email.replace('.localhost:3000', '.com') };
        }
        return user;
      },
      transformErrorResponse: error => {
        // If we get a 401, the user is no longer authenticated.
        // The backend would have deleted the cookie, so we need to reload the page to force the user to log in again.
        // RTK-query adds some of its own error wrapping logic. This is us trying to just figure out if the http error (if any) is 401 or not
        if (error && 'response' in error && error.response.status === 401) {
          window.location.reload();
        }
        return error;
      },
      providesTags: ['User'],
    }),
    createApiKey: builder.mutation<ApiKeyResult, { whitelistedIPs?: string }>({
      query: body => ({ url: '/user/api_key', method: 'POST', body }),
      invalidatesTags: ['User'], // clear the User cache, making us re-fetch the user
    }),
    deleteApiKey: builder.mutation<void, void>({
      query: () => ({
        url: '/user/api_key',
        method: 'DELETE',
        // For some reason this is the only way the request is accepted by the backend. If I don't provide the json body and app/json header
        // then it doesn't accept the request, even with body being undefined (we have always sent app/json here for the record, but implicitly)
        body: {},
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: ['User'],
    }),
    updateApiKeyWhitelistedIPs: builder.mutation<void, { whitelistedIPs: string }>({
      query: body => ({ url: '/user/api_key/whitelist', method: 'PUT', body }),
      invalidatesTags: ['User'],
    }),
    updateApiKeyRoles: builder.mutation<void, { roles: string[] }>({
      query: body => ({ url: '/user/api_key/roles', method: 'PUT', body }),
      invalidatesTags: ['User'],
    }),
  }),
});

interface UserStoreState {
  [userApi.reducerPath]: ReturnType<typeof userApi.reducer>;
}

export function getUserStore() {
  return configureStore({
    reducer: {
      [userApi.reducerPath]: userApi.reducer,
    },
    // serializableCheck false because we return Error objects, which are then stored by rtk-query in state...
    middleware: getDefaultMiddleware => getDefaultMiddleware({ serializableCheck: false }).concat(userApi.middleware),
  });
}

/** A specific selector to get the User in the outer User Store. You should only use this if you are in the UserContextProvider. */
export const selectUserStoreUser = (state: UserStoreState) => userApi.endpoints.getUser.select()(state)?.data;
